import React, { useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Badge, Spinner, Alert } from "react-bootstrap";
import ManageBillingButton from "./ManageBillingButton.tsx";
import { BillingClient, FileResponse, StartCheckoutDto } from "../web-api-client.ts";

type BillingStatus = "pending_setup" | "trialing" | "active" | "past_due" | "canceled";
type StatusResponse = {
  status: BillingStatus;
  amountCents: number;
  currency: string;
  currentPeriodEnd?: string | null;
  trialEnd?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
};

async function fileResponseToJson<T>(fr: FileResponse): Promise<T> {
  const text = await fr.data.text();
  return JSON.parse(text) as T;
}

const BillingPage: React.FC = () => {
  const billingClient = useMemo(() => new BillingClient(), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<StatusResponse | null>(null);

  async function load() {
    setErr(null);
    setLoading(true);
    try {
      // If you generated this in the client, call that; else fetch:
      const res = await fetch("/api/Billing/status", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const d: StatusResponse = await res.json();
      setData(d);
    } catch (e: any) {
      setErr(e.message || "Failed to load billing status.");
    } finally {
      setLoading(false);
    }
  }

  async function startCheckout() {
    setErr(null);
    setSaving(true);
    try {
      const dto: StartCheckoutDto = { dealerId: undefined as any };
      const fr = await billingClient.billing_StartCheckout(dto);
      const { sessionUrl } = await fileResponseToJson<{ sessionUrl: string }>(fr);
      window.location.href = sessionUrl;
    } catch (e: any) {
      setErr(e.message ?? "Failed to start checkout.");
      setSaving(false);
    }
  }

  useEffect(() => { load(); }, []);

  const prettyMoney = (cents?: number, ccy = "usd") =>
    typeof cents === "number" ? (cents / 100).toLocaleString(undefined, { style: "currency", currency: ccy.toUpperCase() }) : "";

  const statusBadge = (s?: BillingStatus) => {
    if (!s) return null;
    const map: Record<BillingStatus, string> = {
      active: "success",
      trialing: "info",
      past_due: "warning",
      canceled: "secondary",
      pending_setup: "secondary",
    };
    return <Badge bg={map[s]} className="ms-2 text-uppercase">{s.replace("_", " ")}</Badge>;
  };

  return (
    <div className="container py-4">
      <h2 className="mb-3">Billing</h2>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" role="status" />
          <div>Loading…</div>
        </div>
      ) : data && (
        <Card className="p-3">
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <h4 className="mb-0">
                  Subscription {statusBadge(data.status)}
                </h4>
              </div>
              <div className="text-muted mt-2">
                <div>Plan amount: <strong>{prettyMoney(data.amountCents, data.currency)}</strong> billed monthly</div>
                {data.currentPeriodEnd && (
                  <div>Current period ends: <strong>{new Date(data.currentPeriodEnd).toLocaleString()}</strong></div>
                )}
                {data.trialEnd && (
                  <div>Trial ends: <strong>{new Date(data.trialEnd).toLocaleString()}</strong></div>
                )}
              </div>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0 d-flex gap-2 justify-content-md-end">
              {(data.status === "pending_setup" || data.status === "past_due" || data.status === "canceled") ? (
                <Button onClick={startCheckout} disabled={saving}>
                  {saving ? "Redirecting…" : "Complete Payment"}
                </Button>
              ) : null}
              {/* Portal is useful in any state */}
              <ManageBillingButton />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default BillingPage;
